import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BuildIcon from '@mui/icons-material/Build';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, IconButton, Link, Stack, Tooltip, Typography, alpha } from '@mui/material';

import MarkdownPreview from '~/components/Common/MarkdownPreview';

import { useAppSelector } from '~/hooks/store';
import useCurrentUser from '~/hooks/useCurrentUser';

import { customerPortalHost } from '~/services/common';

import { ChatMessage, ThreadContextSnapshot, ToolCallInfo } from './AiChatContext';
import {
  buildAssistantCopyText,
  buildMyEscapesOrderUrl,
  getDepartmentForAssistantMessage,
  getPrecedingUserMessageContent,
  isCustomerFacingDraftQuery,
  messageAlreadyContainsMyEscapesOrderLink,
} from './aiChatMessageUtils';
import { DEPARTMENT_OPTIONS } from './chatModes';

interface AiChatMessageListProps {
  threadId: string;
  messages: Array<ChatMessage>;
  isLoading: boolean;
  orderId: string | null;
  department: string | null;
}

const markdownSx = {
  '& p': { m: 0 },
  '& p + p': { mt: 1 },
  '& ul, & ol': { m: 0, pl: 2.5 },
  '& li': { mb: 0.25 },
  '& pre': {
    bgcolor: 'grey.200',
    p: 1,
    borderRadius: 1,
    overflow: 'auto',
    fontSize: '0.8rem',
  },
  '& code': { fontSize: '0.85em' },
  '& a': { color: 'primary.main' },
  '& table': { borderCollapse: 'collapse', width: '100%', my: 1 },
  '& th, & td': { border: 1, borderColor: 'divider', px: 1, py: 0.5, fontSize: '0.8rem' },
  '& blockquote': { borderLeft: 3, borderColor: 'divider', pl: 1.5, ml: 0, my: 1, color: 'text.secondary' },
  '& h1, & h2, & h3, & h4': { mt: 1.5, mb: 0.5, fontSize: '0.95rem', fontWeight: 'bold' },
};

const ASSISTANT_MESSAGE_MAX_WIDTH = 720;
const TOOL_CALL_MAX_WIDTH = 480;
const AUTO_SCROLL_BOTTOM_THRESHOLD_PX = 32;
const SCROLL_POSITION_MEMORY_MS = 5 * 60 * 1000;
const SCROLL_POSITION_GC_MIN_ENTRIES = 20;
const CONTEXT_CARD_ALLOWED_USERS = new Set([
  'andy.welch@luxuryescapes.com',
  'eugene.moskvita@luxuryescapes.com',
  'samuel.drown@luxuryescapes.com',
  'sitaraman.padmanabhan@luxuryescapes.com',
  'william.ritossa@luxuryescapes.com',
  'joshua.paling@luxuryescapes.com',
  'nicholas.puglia@luxuryescapes.com',
]);

const threadScrollPositions = new Map<string, { scrollTop: number; updatedAt: number }>();

function findScrollParent(node: HTMLElement | null): HTMLElement | null {
  let current = node?.parentElement || null;

  while (current) {
    const overflowY = window.getComputedStyle(current).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return current;
    }

    current = current.parentElement;
  }

  return null;
}

function isNearScrollBottom(element: HTMLElement): boolean {
  return element.scrollHeight - element.scrollTop - element.clientHeight <= AUTO_SCROLL_BOTTOM_THRESHOLD_PX;
}

function scrollToBottom(element: HTMLElement) {
  element.scrollTop = element.scrollHeight;
}

function ToolCallItem({ toolCall }: { toolCall: ToolCallInfo }) {
  const [expanded, setExpanded] = useState(false);
  const hasSummary = toolCall.summary && toolCall.summary.length > 0;

  const handleToggle = useCallback(() => {
    if (hasSummary) {
      setExpanded((prev) => !prev);
    }
  }, [hasSummary]);

  return (
    <Box
      sx={{
        width: 'fit-content',
        minWidth: 240,
        maxWidth: '100%',
        border: 1,
        borderColor: (theme) => alpha(theme.palette.divider, 0.7),
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: (theme) => alpha(theme.palette.text.primary, 0.025),
      }}
    >
      <Box
        onClick={handleToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          cursor: hasSummary ? 'pointer' : 'default',
          '&:hover': hasSummary ? { bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04) } : undefined,
        }}
      >
        <BuildIcon sx={{ fontSize: 13, color: 'text.secondary', opacity: 0.7 }} />
        <Typography variant="caption" sx={{ fontWeight: 500, flex: 1, color: 'text.secondary', fontSize: '0.72rem' }}>
          Reviewed · {toolCall.label}
        </Typography>
        {hasSummary &&
          (expanded ? (
            <ExpandLessIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
          ))}
      </Box>
      {hasSummary && (
        <Collapse in={expanded}>
          <Box
            sx={{
              px: 1.25,
              py: 0.75,
              borderTop: 1,
              borderColor: (theme) => alpha(theme.palette.divider, 0.6),
            }}
          >
            {toolCall.summary.map((field, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, py: 0.125 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80, flexShrink: 0 }}>
                  {field.label}:
                </Typography>
                <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                  {field.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

function ToolCallIndicators({ toolCalls }: { toolCalls: Array<ToolCallInfo> }) {
  return (
    <Stack spacing={0.5} sx={{ mb: 1, maxWidth: TOOL_CALL_MAX_WIDTH }}>
      {toolCalls.map((tc, i) => (
        <ToolCallItem key={`${tc.name}-${i}`} toolCall={tc} />
      ))}
    </Stack>
  );
}

function DepartmentIndicator({ department }: { department: string }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        mb: 1,
        borderRadius: 999,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
        color: 'primary.main',
        border: 1,
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
      }}
    >
      <CorporateFareOutlinedIcon sx={{ fontSize: 12 }} />
      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.68rem', letterSpacing: 0.2, lineHeight: 1.4 }}>
        {department.toUpperCase()}
      </Typography>
    </Box>
  );
}

function ContextMessageCard({ contextSnapshot }: { contextSnapshot: ThreadContextSnapshot | null | undefined }) {
  const [expanded, setExpanded] = useState(false);
  const handleToggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  if (!contextSnapshot) {
    return null;
  }

  const rows = [
    ['Customer ID', contextSnapshot.customerId],
    ['Customer name', contextSnapshot.customerName],
    ['Order ID', contextSnapshot.orderId],
    ['Department', contextSnapshot.department],
    ['Mode', contextSnapshot.mode],
    ['Page', contextSnapshot.page],
  ].filter(([, value]) => Boolean(value));

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', my: 0.5 }}>
      <Box sx={{ width: '100%', maxWidth: TOOL_CALL_MAX_WIDTH }}>
        <Box
          onClick={handleToggle}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            color: 'text.secondary',
            transition: 'color 120ms ease',
            '&:hover': { color: 'text.primary' },
          }}
        >
          <Box
            sx={{
              flex: 1,
              height: '1px',
              bgcolor: (theme) => alpha(theme.palette.divider, 0.6),
            }}
          />
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CorporateFareOutlinedIcon sx={{ fontSize: 12, opacity: 0.7 }} />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.68rem',
                fontWeight: 500,
                letterSpacing: 0.3,
                textTransform: 'uppercase',
              }}
            >
              Context updated
            </Typography>
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: 13, opacity: 0.7 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 13, opacity: 0.7 }} />
            )}
          </Stack>
          <Box
            sx={{
              flex: 1,
              height: '1px',
              bgcolor: (theme) => alpha(theme.palette.divider, 0.6),
            }}
          />
        </Box>
        <Collapse in={expanded}>
          <Box
            sx={{
              mt: 1,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              bgcolor: (theme) => alpha(theme.palette.text.primary, 0.03),
              border: 1,
              borderColor: (theme) => alpha(theme.palette.divider, 0.5),
            }}
          >
            {rows.map(([label, value]) => (
              <Box key={label} sx={{ display: 'flex', gap: 1, py: 0.125 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ minWidth: 96, flexShrink: 0, fontSize: '0.7rem' }}
                >
                  {label}
                </Typography>
                <Typography variant="caption" sx={{ wordBreak: 'break-word', fontSize: '0.7rem' }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

function AssistantMessageBody({
  message,
  messageIndex,
  messages,
  orderId,
  customerPortalBase,
  department,
}: {
  message: ChatMessage;
  messageIndex: number;
  messages: Array<ChatMessage>;
  orderId: string | null;
  customerPortalBase: string | null;
  department: string | null;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const precedingUser = getPrecedingUserMessageContent(messages, messageIndex);
  const activeDepartment = getDepartmentForAssistantMessage(messages, messageIndex, DEPARTMENT_OPTIONS, department);
  const draftStyleQuery = isCustomerFacingDraftQuery(precedingUser);
  const myEscapesUrl = orderId && customerPortalBase ? buildMyEscapesOrderUrl(customerPortalBase, orderId) : null;
  const showMyEscapesBlock =
    Boolean(draftStyleQuery && orderId && myEscapesUrl) &&
    !messageAlreadyContainsMyEscapesOrderLink(message.content, orderId);

  const myEscapesAppendix = showMyEscapesBlock && myEscapesUrl ? `My Escapes (this booking): ${myEscapesUrl}` : null;

  const handleCopyAll = useCallback(async () => {
    const text = buildAssistantCopyText(message.content, myEscapesAppendix);
    try {
      await navigator.clipboard.writeText(text);
      enqueueSnackbar('Copied to clipboard', { variant: 'success' });
    } catch {
      enqueueSnackbar('Could not copy', { variant: 'error' });
    }
  }, [enqueueSnackbar, message.content, myEscapesAppendix]);

  return (
    <>
      {activeDepartment && <DepartmentIndicator department={activeDepartment} />}
      {message.toolCalls && message.toolCalls.length > 0 && <ToolCallIndicators toolCalls={message.toolCalls} />}
      <Box sx={{ ...markdownSx, fontSize: '0.875rem', wordBreak: 'break-word' }}>
        <MarkdownPreview content={message.content} />
      </Box>
      {showMyEscapesBlock && myEscapesUrl && (
        <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            My Escapes (this booking)
          </Typography>
          <Link href={myEscapesUrl} target="_blank" rel="noreferrer" variant="body2" sx={{ wordBreak: 'break-all' }}>
            {myEscapesUrl}
          </Link>
        </Box>
      )}
      {message.content && message.content.length > 0 && (
        <Box
          className="ai-chat-assistant-actions"
          sx={{
            mt: 0.5,
            ml: -0.5,
            opacity: 0,
            transition: 'opacity 160ms ease-in-out',
          }}
        >
          <Tooltip title="Copy reply" placement="top">
            <IconButton
              size="small"
              onClick={handleCopyAll}
              aria-label="Copy reply"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: (theme) => alpha(theme.palette.text.primary, 0.06),
                },
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </>
  );
}

export default function AiChatMessageList({
  threadId,
  messages,
  isLoading,
  orderId,
  department,
}: AiChatMessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);
  const suppressNextAutoScrollRef = useRef(false);
  const tenant = useAppSelector((state) => state.tenant);
  const { user } = useCurrentUser();
  const normalizedEmail = user?.email?.trim().toLowerCase() || '';
  const canViewContextCards = CONTEXT_CARD_ALLOWED_USERS.has(normalizedEmail);

  const customerPortalBase = useMemo(() => {
    const fromTenant = customerPortalHost(tenant);
    const raw = (typeof fromTenant === 'string' && fromTenant ? fromTenant : window.configs?.LUX_CUSTOMER_PORTAL) || '';
    return raw ? raw.replace(/\/$/, '') : null;
  }, [tenant]);

  useLayoutEffect(() => {
    const scrollParent = findScrollParent(bottomRef.current);

    if (!scrollParent) {
      return;
    }

    const rememberedPosition = threadScrollPositions.get(threadId);
    const hasFreshRememberedPosition =
      rememberedPosition && Date.now() - rememberedPosition.updatedAt <= SCROLL_POSITION_MEMORY_MS;

    if (hasFreshRememberedPosition) {
      scrollParent.scrollTop = Math.min(
        rememberedPosition.scrollTop,
        Math.max(0, scrollParent.scrollHeight - scrollParent.clientHeight),
      );
      shouldAutoScrollRef.current = isNearScrollBottom(scrollParent);
    } else {
      scrollToBottom(scrollParent);
      shouldAutoScrollRef.current = true;
    }

    suppressNextAutoScrollRef.current = true;
  }, [threadId]);

  useEffect(() => {
    const scrollParent = findScrollParent(bottomRef.current);

    if (!scrollParent) {
      return;
    }

    function handleScroll() {
      const now = Date.now();
      if (threadScrollPositions.size > SCROLL_POSITION_GC_MIN_ENTRIES) {
        threadScrollPositions.forEach((position, positionThreadId) => {
          if (now - position.updatedAt > SCROLL_POSITION_MEMORY_MS) {
            threadScrollPositions.delete(positionThreadId);
          }
        });
      }
      threadScrollPositions.set(threadId, {
        scrollTop: scrollParent.scrollTop,
        updatedAt: now,
      });
      shouldAutoScrollRef.current = isNearScrollBottom(scrollParent);
    }

    scrollParent.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollParent.removeEventListener('scroll', handleScroll);
  }, [threadId]);

  useEffect(() => {
    if (suppressNextAutoScrollRef.current) {
      suppressNextAutoScrollRef.current = false;
      return;
    }

    if (shouldAutoScrollRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  if (messages.length === 0 && !isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          px: 4,
          textAlign: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 48,
            height: 48,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: (theme) =>
              `radial-gradient(circle at 30% 30%, ${alpha(theme.palette.primary.light, 0.9)}, ${
                theme.palette.primary.main
              } 70%)`,
            color: 'primary.contrastText',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              boxShadow: (theme) => `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.22)}`,
              pointerEvents: 'none',
            },
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', letterSpacing: -0.3, lineHeight: 1.2 }}>
            How can I help today?
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5, fontSize: '0.78rem', letterSpacing: 0.1, opacity: 0.85 }}
          >
            Ask about an order, customer, or offer.
          </Typography>
        </Box>
      </Box>
    );
  }

  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const isAssistantStreamingContent =
    lastMessage?.role === 'assistant' && Boolean(lastMessage.content && lastMessage.content.length > 0);
  const showThinkingDots = isLoading && !isAssistantStreamingContent;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, px: 2, pt: 2, pb: 1 }}>
      {messages.map((message, index) => {
        const isUser = message.role === 'user';
        const isContext = message.role === 'context';
        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: isUser ? 'flex-end' : 'flex-start',
              mb: isUser ? 0.5 : 0,
              '&:hover .ai-chat-assistant-actions': {
                opacity: 1,
              },
            }}
          >
            {isContext && canViewContextCards ? (
              <ContextMessageCard contextSnapshot={message.contextSnapshot} />
            ) : isContext ? null : isUser ? (
              <Box
                sx={{
                  px: 1.75,
                  py: 1.125,
                  maxWidth: '82%',
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.08)
                      : alpha(theme.palette.text.primary, 0.05),
                  color: 'text.primary',
                  borderRadius: '18px 18px 4px 18px',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.5,
                  }}
                >
                  {message.content}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: ASSISTANT_MESSAGE_MAX_WIDTH,
                  color: 'text.primary',
                  lineHeight: 1.55,
                }}
              >
                <AssistantMessageBody
                  message={message}
                  messageIndex={index}
                  messages={messages}
                  orderId={orderId}
                  customerPortalBase={customerPortalBase}
                  department={department}
                />
              </Box>
            )}
          </Box>
        );
      })}

      {showThinkingDots && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1, py: 0.5 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              alignItems: 'center',
              '& > span': {
                width: 5,
                height: 5,
                borderRadius: '50%',
                bgcolor: 'text.secondary',
                animation: 'aiChatPulse 1.4s ease-in-out infinite',
              },
              '& > span:nth-of-type(2)': { animationDelay: '0.2s' },
              '& > span:nth-of-type(3)': { animationDelay: '0.4s' },
              '@keyframes aiChatPulse': {
                '0%, 80%, 100%': { opacity: 0.25 },
                '40%': { opacity: 0.9 },
              },
            }}
          >
            <span />
            <span />
            <span />
          </Box>
        </Box>
      )}

      <Box ref={bottomRef} />
    </Box>
  );
}
